// ink-component
import React, { Component } from 'react';

import onClickOutside from 'react-onclickoutside';
import moment from 'moment';
import NumberFormat from 'react-number-format';
import shortid from 'shortid';

import Control from '../Control/Control';
import Calendar from './Calendar';

import { bemHelper } from '../../helpers';

import { defaultShortcuts } from './DatePickerTypes';
import { getMask } from '../_common/CoreInput/getMask';

import getGlobalProps from '../../helpers/constants/global-props';

const getDateMaskLength = dateMask => dateMask.replace(/\//g, '').length;
const onChangeToEvent = (value, id) => ({ target: { value, id } });

export interface DatePickerProps {
  /** Unique identifier */
  id: string;
  /** Unique name */
  name?: string;
  /** Date mask, passing MM/YYYY hides the days in the calendar */
  dateMask?: 'MM/DD/YYYY' | 'MM/YYYY';
  /** Input placeholder */
  placeholder?: string;
  /** HTML type */
  autoFocus?: boolean;
  /** External control and initial value for the DatePicker */
  value?: Date | moment.Moment | string;
  /** Size modifier */
  size?: '' | 'full' | 'half' | 'quarter' | 'auto';
  /** Modifier for tables */
  isCell?: boolean;
  /** onFocus callback */
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  /** onChange callback */
  onChange?: (pseudoEvent: { target: { value: string; id: string } }) => void;
  /** onBlur callback */
  onBlur?: (value: string) => void;
  /** onKeyDown callback */
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  /** Modifier for inline DatePicker */
  isInline?: boolean;
  /** Modifier for not valid state */
  isInvalid?: boolean;
  /** Modifier for disabled state */
  isDisabled?: boolean;
  /** Modifier for right aligned calendars */
  isRightAligned?: boolean;
  /** Flag for calendars with shortcuts */
  hasShortcuts?: boolean;
  /** Flag to show or not the calendar itself */
  hasCalendar?: boolean;
  /** Array of shortcuts that will be created dynamically.
   * Defaults to: today, last month, last quarter and last year */
  shortcuts: {
    label: React.ReactNode;
    hotkey: string;
    date: Date | moment.Moment | string;
  }[];
  /** Limit start date for valid dates on the calendar. You must display limitStartDate as help content when used inside a Field. */
  limitStartDate?: Date | moment.Moment | string;
  /** Limit end date for valid dates on the calendar. You must display limitEndDate as help content when used inside a Field. */
  limitEndDate?: Date | moment.Moment | string;
  /** Define the dates that can be selected. The function receives
   * (currentDate, selectedDate) and should return a true or false
   * whether the currentDate is valid or not. */
  isValidDate: (day: Date | moment.Moment | string, selectedDate: Date | moment.Moment | string) => boolean;
  /** Marks a field as private to keep trackers from logging user input */
  dataPrivate?: string | boolean;
  /** Set [autoComplete](/patterns/forms/#autocomplete) */
  autoComplete?: string;
  /** Allows screen readers to describe the content. The correct use of Field excludes the need for aria-label */
  'aria-label'?: string;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
  /** @ignore */
  defaultValue?: string;
  /** @ignore */
  meta: {
    initial: Date | moment.Moment | string;
    invalid: boolean;
  };
  /** @ignore */
  input: {
    onFocus: (event: React.FocusEvent<HTMLInputElement>) => void;
    onChange: (value: string) => void;
    onBlur: (value: string) => void;
    value: Date | moment.Moment | string;
  };
}

interface DatePickerState {
  value: Date | moment.Moment | string;
  isOpen: boolean;
}

const globalProps = getGlobalProps({ deprecated: true });

/**
 * @aka calendar
 * @categories form
 * @purpose Allows users to choose dates, with optional help from shortcuts
 * @deprecated This component is deprecated and replaced by [NewDatePicker](https://ink.carta.com/components/NewDatePicker/)
 * @status deprecated
 * @replacementComponent NewDatePicker
 * @dateOfDeletion September 1st, 2023
 */
class DatePicker extends Component<DatePickerProps, DatePickerState> {
  shouldHandleClickOutside: boolean;

  mask: string;

  name: string;

  datePickerInput: HTMLInputElement;

  constructor(props: DatePickerProps) {
    super(props);

    this.state = {
      isOpen: false,
      value: this.defineInitialValue(),
    };

    this.shouldHandleClickOutside = false;

    this.mask = props.dateMask === 'MM/DD/YYYY' ? 'date' : 'short-date';

    this.name = `${props.id}-${shortid.generate()}`;

    this.updateRef = this.updateRef.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.isValidDate = this.isValidDate.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleValueChange = this.handleValueChange.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.toggleCalendar = this.toggleCalendar.bind(this);
    this.getFormattedValue = this.getFormattedValue.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.handleCalendarChange = this.handleCalendarChange.bind(this);
    this.focusDatePickerInput = this.focusDatePickerInput.bind(this);
  }

  componentDidMount() {
    if (this.props.autoFocus) {
      setTimeout(() => {
        this.focusDatePickerInput();
      }, 0);
    }
  }

  convertValue = value => (typeof value === 'string' ? value : moment(value).format(this.props.dateMask));

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.input.value !== nextProps.input.value) {
      this.setState({
        value: this.convertValue(nextProps.input.value),
      });
    } else if (this.props.value !== nextProps.value) {
      this.setState({
        value: this.convertValue(nextProps.value),
      });
    }
  }

  getFormattedValue() {
    if (this.isValidDate(this.state.value)) {
      return this.formatDate(this.state.value);
    }
    return '';
  }

  updateRef(el) {
    this.datePickerInput = el;
  }

  defineInitialValue() {
    const { defaultValue, value, input, meta } = this.props;

    // If the user - for some twisted reason - passes the defaultValue prop
    // as `null` moment breaks, so we need to check a third time to make it
    // a string
    const initialValue = input.value || meta.initial || defaultValue || value || '';

    return this.convertValue(initialValue);
  }

  defineClassName() {
    const b = bemHelper();

    return b('ink-control--input', {
      cell: this.props.isCell,
    });
  }

  toggleCalendar() {
    // Call the onBlur event when the Calendar closes
    if (this.state.isOpen) {
      setTimeout(() => {
        this.handleBlur();
      }, 0);
    }
    this.setState(prevState => ({
      isOpen: !prevState.isOpen,
    }));
  }

  focusDatePickerInput() {
    this.datePickerInput.focus();
  }

  formatDate(value) {
    return moment(value, this.props.dateMask).format(this.props.dateMask);
  }

  isValidDate(value) {
    return moment(value, this.props.dateMask).isValid();
  }

  formatInput() {
    if (this.isValidDate(this.state.value)) {
      this.setState(prevState => ({
        value: this.formatDate(prevState.value),
      }));
    } else {
      this.setState({
        value: '',
      });
    }
  }

  handleClickOutside() {
    if (this.state.isOpen) {
      this.focusDatePickerInput();
      this.toggleCalendar();
    }
    if (this.shouldHandleClickOutside) {
      this.formatInput();
      this.shouldHandleClickOutside = false;
    }
  }

  handleKeyDown(event) {
    if (event.key === 'Enter' || event.key === 'Tab') {
      if (this.props.input.onChange) {
        this.props.input.onChange(this.getFormattedValue());
      }
      this.formatInput();
    }
    this.props.onKeyDown && this.props.onKeyDown(event);
  }

  handleFocus(e) {
    const { input, onFocus } = this.props;
    if (input.onFocus) {
      input.onFocus(e);
    }
    if (onFocus) {
      onFocus(e);
    }
    this.shouldHandleClickOutside = true;
  }

  handleChange({ formattedValue }) {
    if (formattedValue) {
      const { input, onChange, id } = this.props;

      if (input.onChange) {
        input.onChange(formattedValue);
      }
      if (onChange) {
        onChange(onChangeToEvent(formattedValue, id));
      }

      this.setState({
        value: formattedValue,
      });
    }
  }

  handleValueChange(prop) {
    const { input, onChange, dateMask, id } = this.props;

    const value = prop.value.length < getDateMaskLength(dateMask) ? prop.value : prop.formattedValue;

    if (input.onChange) {
      input.onChange(value);
    }
    if (onChange) {
      onChange(onChangeToEvent(value, id));
    }

    this.setState({
      value,
    });
  }

  // TODO: Think of a better way to send this data so I don't need to simulate an event
  handleCalendarChange(value) {
    this.setState({ value: '' }, () => this.handleChange({ formattedValue: value }));
  }

  handleBlur() {
    const { input, onBlur } = this.props;

    if (input.onBlur) {
      input.onBlur(this.getFormattedValue());
    }
    if (onBlur) {
      onBlur(this.getFormattedValue());
    }
  }

  defineButtonClassName() {
    const b = bemHelper();

    return b('ink-control--action', {
      open: this.state.isOpen,
      cell: this.props.isCell,
    });
  }

  render() {
    return (
      <Control
        type="date"
        inputId={this.props.id}
        isCell={this.props.isCell}
        isInline={this.props.isInline}
        isInvalid={this.props.isInvalid}
        isDisabled={this.props.isDisabled}
        size={this.props.size}
        dataPrivate={this.props.dataPrivate}
        data-testid={this.props['data-testid']}
      >
        <NumberFormat
          getInputRef={this.updateRef}
          id={this.props.id}
          name={this.props.name || this.name}
          aria-label={this.props['aria-label']}
          autoComplete={this.props.autoComplete}
          className={this.defineClassName()}
          value={this.state.value}
          placeholder={this.props.placeholder}
          onFocus={this.handleFocus}
          onChange={this.handleChange}
          onValueChange={this.handleValueChange}
          onBlur={this.handleBlur}
          onKeyDown={this.handleKeyDown}
          disabled={this.props.isDisabled}
          autoFocus={this.props.autoFocus}
          {...getMask(this.mask)}
          {...globalProps}
        />
        <button
          className={this.defineButtonClassName()}
          onClick={this.toggleCalendar}
          type="button"
          disabled={this.props.isDisabled}
        >
          <div className="ink-calendar--icon" />
        </button>
        <Calendar
          value={this.state.value}
          isOpen={this.state.isOpen}
          isCell={this.props.isCell}
          isRightAligned={this.props.isRightAligned}
          onChange={this.handleCalendarChange}
          dateMask={this.props.dateMask}
          hasShortcuts={this.props.hasShortcuts}
          hasCalendar={this.props.hasCalendar}
          shortcuts={this.props.shortcuts}
          limitStartDate={this.props.limitStartDate}
          limitEndDate={this.props.limitEndDate}
          closeCalendar={this.handleClickOutside}
          isValidDate={this.props.isValidDate}
        />
      </Control>
    );
  }

  static defaultProps = {
    name: null,
    dateMask: 'MM/DD/YYYY',
    placeholder: ' ', // Adds character so Safari knows how to align by baseline
    defaultValue: '',
    value: '',
    size: 'half',
    onFocus: null,
    onChange: null,
    onBlur: null,
    onKeyDown: () => {},
    isCell: false,
    isInline: false,
    isInvalid: false,
    isDisabled: false,
    isRightAligned: false,
    hasShortcuts: false,
    hasCalendar: true,
    shortcuts: defaultShortcuts,
    limitStartDate: null,
    limitEndDate: null,
    isValidDate: () => true,
    autoFocus: false,
    dataPrivate: undefined,
    'data-testid': undefined,

    meta: {},
    input: {},
  };

  static displayName = 'DatePicker';
}

export { DatePicker as DatePickerDefault };

export default onClickOutside(DatePicker);
